import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import {
  TrackChanges as TargetIcon,
  MenuBook as BookOpenIcon,
  Shield as ShieldIcon,
  Visibility as EyeIcon,
  EmojiEvents as AwardIcon,
  Warning as AlertCircleIcon,
  Gavel as ScaleIcon,
  ChevronLeft,
  ChevronRight,
  Lightbulb as LightbulbIcon,
  TrendingUp as TrendingUpIcon,
  People as UsersIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const Home = () => {
  const [currentStoryCard, setCurrentStoryCard] = useState(0);

  const storyCards = [
    {
      icon: AlertCircleIcon,
      title: 'The Problem',
      description:
        'Citizens across Tamil Nadu were tired of being exploited by corrupt officials who demanded bribes for basic services—from getting a driver\'s license to registering property, from admitting a patient to a government hospital to securing a ration card.',
      color: 'error',
    },
    {
      icon: LightbulbIcon,
      title: 'The Beginning',
      description:
        'What started as informal conversations among concerned citizens in Chennai has grown into a state-wide movement. We realized that individual complaints were not enough—we needed a systematic, organized approach to tackle corruption at its roots.',
      color: 'warning',
    },
    {
      icon: TrendingUpIcon,
      title: 'Our Growth',
      description:
        'Today, the Chennai Anti Bribe Movement stands as a beacon of hope for thousands of citizens. We have successfully resolved hundreds of complaints, conducted awareness camps across Tamil Nadu, and built a network of volunteers and legal experts.',
      color: 'info',
    },
    {
      icon: UsersIcon,
      title: 'The Future',
      description:
        'Our journey has just begun. With every complaint we resolve, every official we hold accountable, and every citizen we empower, we move one step closer to a corruption-free Tamil Nadu.',
      color: 'success',
    },
  ];

  const nextCard = () => {
    setCurrentStoryCard((prev) => (prev + 1) % storyCards.length);
  };

  const prevCard = () => {
    setCurrentStoryCard((prev) => (prev - 1 + storyCards.length) % storyCards.length);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 16, lg: 24 },
          minHeight: { xs: '600px', lg: '700px' },
        }}
      >
        {/* Background Image Slideshow */}
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 10 }} />

          {['/images/image1.jpeg', '/images/image2.jpeg', '/images/image3.jpeg'].map((image, index) => (
            <motion.div
              key={index}
              animate={{
                opacity: index === 0 ? [1, 0, 0, 1] : index === 1 ? [0, 1, 0, 0] : [0, 0, 1, 0],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            >
              <Box
                component="img"
                src={image}
                alt={`Slide ${index + 1}`}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </motion.div>
          ))}
        </Box>

        {/* Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 20 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h1"
                sx={{
                  color: 'white',
                  mb: 3,
                  textShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                  fontSize: { xs: '2.5rem', md: '3.75rem' },
                }}
              >
                Chennai Anti Bribe Movement
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  mb: 2,
                  textShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                }}
              >
                Towards a Corrupt-Free Tamil Nadu
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  textShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                }}
              >
                லஞ்சம் இல்லாத தமிழ்நாடு
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Mission & Vision */}
      <Box sx={{ py: 8, backgroundColor: '#f9fafb' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} lg={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%', borderLeft: 4, borderColor: 'primary.main' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <TargetIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                      <Typography variant="h3">Our Mission</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                      To empower citizens to fight corruption and eradicate bribery in public
                      services across Tamil Nadu. We provide the tools, support, and legal backing
                      needed to report corruption fearlessly and hold accountable those who demand or
                      accept bribes.
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      Through awareness campaigns, legal action, and community engagement, we strive
                      to promote transparency and accountability in government and public
                      institutions.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%', borderLeft: 4, borderColor: 'secondary.main' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <EyeIcon sx={{ fontSize: 32, color: 'secondary.main', mr: 2 }} />
                      <Typography variant="h3">Our Vision</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                      A transparent, accountable Tamil Nadu where public services are delivered with
                      integrity and dignity. We envision a future where no citizen has to pay a bribe
                      to access their fundamental rights and services.
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      Through sustained efforts and collective action, we aim to create a culture
                      where corruption is not tolerated, and government officials serve the people
                      with honesty and dedication.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Story - Carousel */}
      <Box sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <BookOpenIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h2" sx={{ mb: 3 }}>
                Our Story
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                From grassroots frustration to a powerful movement for change
              </Typography>
            </Box>
          </motion.div>

          {/* Carousel Container */}
          <Box>
            <motion.div
              key={currentStoryCard}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
            >
              <Card sx={{ mb: 4, overflow: 'hidden', boxShadow: 3 }}>
                <Grid container>
                  {/* Icon Section */}
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: { xs: 4, md: 6 },
                      background: (theme) =>
                        `linear-gradient(135deg, ${
                          theme.palette[storyCards[currentStoryCard].color].main
                        }, ${theme.palette[storyCards[currentStoryCard].color].dark})`,
                    }}
                  >
                    {React.createElement(storyCards[currentStoryCard].icon, {
                      sx: { fontSize: { xs: 96, md: 128 }, color: 'white' },
                    })}
                  </Grid>

                  {/* Content Section */}
                  <Grid item xs={12} md={8}>
                    <CardContent sx={{ p: { xs: 4, md: 6 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                      <Typography variant="h3" sx={{ mb: 3 }}>
                        {storyCards[currentStoryCard].title}
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}>
                        {storyCards[currentStoryCard].description}
                      </Typography>
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>
            </motion.div>

            {/* Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <IconButton
                onClick={prevCard}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'primary.dark' },
                  boxShadow: 3,
                }}
              >
                <ChevronLeft />
              </IconButton>

              {/* Dots Indicator */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {storyCards.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentStoryCard(index)}
                    sx={{
                      height: 12,
                      width: index === currentStoryCard ? 32 : 12,
                      borderRadius: 6,
                      backgroundColor: index === currentStoryCard ? 'primary.main' : '#d1d5db',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': { backgroundColor: index === currentStoryCard ? 'primary.main' : '#9ca3af' },
                    }}
                  />
                ))}
              </Box>

              <IconButton
                onClick={nextCard}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'primary.dark' },
                  boxShadow: 3,
                }}
              >
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Counter */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body1" fontWeight={500} color="text.secondary">
                {currentStoryCard + 1} / {storyCards.length}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Core Values */}
      <Box sx={{ py: 8, backgroundColor: '#f9fafb' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h2" sx={{ mb: 3 }}>
                Core Values
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                These principles guide our fight against corruption and our commitment to
                transparency and justice.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            {[
              { icon: ShieldIcon, title: 'Transparency', desc: 'Operating with complete openness in all our activities and decisions.', color: 'primary.main', delay: 0.1 },
              { icon: ScaleIcon, title: 'Accountability', desc: 'Holding ourselves and others responsible for actions and outcomes.', color: 'secondary.main', delay: 0.2 },
              { icon: AwardIcon, title: 'Integrity', desc: 'Upholding the highest ethical standards in our fight against corruption.', color: 'info.main', delay: 0.3 },
              { icon: AlertCircleIcon, title: 'Fearlessness', desc: 'Standing up against corruption without intimidation or compromise.', color: 'success.main', delay: 0.4 },
            ].map((value, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: value.delay }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      transition: 'box-shadow 0.3s',
                      '&:hover': { boxShadow: 6 },
                    }}
                  >
                    <CardContent>
                      <value.icon sx={{ fontSize: 64, color: value.color, mb: 3 }} />
                      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                        {value.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                        {value.desc}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;

